import React, { lazy, Suspense, useEffect, useLayoutEffect, useRef } from 'react';
import { Routes, Route, useLocation, useNavigation } from 'react-router-dom';
import styled from 'styled-components';
import Navbar from './components/common/Navbar';
import Footer from './components/common/Footer';
import LoadingScreen from './components/common/LoadingScreen';
import { LanguageProvider } from './contexts/LanguageContext';
import Favorites from './pages/app/Favorites';
import Settings from './pages/app/Settings';
import Book from './pages/app/Book';
import Activity from './pages/app/Activity';
import EditProfile from './pages/profile/EditProfile';
import SavedAddresses from './pages/profile/SavedAddresses';
import PaymentMethods from './pages/profile/PaymentMethods';
import ChangePassword from './pages/profile/ChangePassword';
import Language from './pages/profile/Language';
import Theme from './pages/profile/Theme';
import Referral from './pages/profile/Referral';
import Legal from './pages/profile/Legal';
import Help from './pages/profile/Help';
import Chat from './pages/profile/help/Chat';
import Phone from './pages/profile/help/Phone';
import Email from './pages/profile/help/Email';
import FAQ from './pages/profile/help/FAQ';

// Lazy loading des pages principales
const Home = lazy(() => import('./pages/Home'));
const NosServices = lazy(() => import('./pages/NosServices'));
const NotreHistoire = lazy(() => import('./pages/Notre-Histoire'));
const NotreFlotte = lazy(() => import('./pages/Notre-Flotte'));
const NotreEquipe = lazy(() => import('./pages/Notre-Equipe'));
const Contact = lazy(() => import('./pages/Contact'));
const DevenirChauffeur = lazy(() => import('./pages/DevenirChauffeur'));
const SearchResults = lazy(() => import('./pages/SearchResults'));
const PaymentPage = lazy(() => import('./pages/PaymentPage'));
const PaymentReturnPage = lazy(() => import('./pages/PaymentReturnPage'));
const PaymentSuccess = lazy(() => import('./pages/PaymentSuccess'));
const TransportStandard = lazy(() => import('./pages/TransportStandard'));
const TransfertsAeroportsGares = lazy(() => import('./pages/TransfertsAeroportsGares'));
const TransfertColis = lazy(() => import('./pages/TransfertColis'));
const MentionsLegales = lazy(() => import('./pages/MentionsLegales'));
const ConditionsGenerales = lazy(() => import('./pages/ConditionsGenerales'));
const PolitiqueConfidentialite = lazy(() => import('./pages/PolitiqueConfidentialite'));
const About = lazy(() => import('./pages/About'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));

// Nouvelles pages d'authentification et tableaux de bord
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const DriverDashboard = lazy(() => import('./pages/DriverDashboard'));
const PassengerDashboard = lazy(() => import('./pages/PassengerDashboard'));
const DriverApplicationForm = lazy(() => import('./pages/DriverApplicationForm'));
const DriverApplicationConfirmation = lazy(() => import('./pages/DriverApplicationConfirmation'));

export function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

const AppContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  position: relative;
  width: 100%;
`;

const MainContent = styled.main`
  padding-top: 80px;
  flex: 1 0 auto;
  width: 100%;
`;

// Composant pour gérer l'affichage ou non du footer et de la navbar
const AppLayout: React.FC<{
  children: React.ReactNode;
  hideFooter?: boolean;
  hideNavbar?: boolean;
}> = ({ children, hideFooter = false, hideNavbar = false }) => {
  return (
    <>
      {!hideNavbar && <Navbar />}
      <MainContent>
        {children}
      </MainContent>
      {!hideFooter && <Footer />}
    </>
  );
};

// Route protégée qui vérifie si l'utilisateur est connecté
const ProtectedRoute: React.FC<{
  children: React.ReactNode;
  userType?: string;
}> = ({ children, userType }) => {
  const navigate = useLocation();
  const [isAuthenticated, setIsAuthenticated] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  
  useEffect(() => {
    const checkAuth = () => {
      const sessionData = localStorage.getItem('userSession');
      
      if (sessionData) {
        const userData = JSON.parse(sessionData);
        
        if (userData.isLoggedIn && (!userType || userData.userType === userType)) {
          setIsAuthenticated(true);
        } else {
          // Rediriger vers la page de connexion
          window.location.href = '/connexion';
        }
      } else {
        // Rediriger vers la page de connexion
        window.location.href = '/connexion';
      }
      
      setIsLoading(false);
    };
    
    checkAuth();
  }, [navigate, userType]);
  
  if (isLoading) {
    return <LoadingScreen />;
  }
  
  return isAuthenticated ? children : null;
};

function App() {

  return (
    <LanguageProvider>
      <AppContainer>
	  
	  <ScrollToTop />
        
        <Suspense fallback={<LoadingScreen />}>
          <Routes>
            {/* Routes principales avec navbar et footer */}
            <Route 
              path="/" 
              element={
                <AppLayout>
                  <Home />
                </AppLayout>
              } 
            />
            <Route 
              path="/services" 
              element={
                <AppLayout>
                  <NosServices />
                </AppLayout>
              } 
            />
            <Route 
              path="/services/transport-standard" 
              element={
                <AppLayout>
                  <TransportStandard />
                </AppLayout>
              } 
            />
            <Route 
              path="/services/transferts-aeroports-gares" 
              element={
                <AppLayout>
                  <TransfertsAeroportsGares />
                </AppLayout>
              } 
            />
            <Route 
              path="/services/transfert-colis" 
              element={
                <AppLayout>
                  <TransfertColis />
                </AppLayout>
              } 
            />
            <Route 
              path="/notre-histoire" 
              element={
                <AppLayout>
                  <NotreHistoire />
                </AppLayout>
              } 
            />
            <Route 
              path="/notre-flotte" 
              element={
                <AppLayout>
                  <NotreFlotte />
                </AppLayout>
              } 
            />
            <Route 
              path="/notre-equipe" 
              element={
                <AppLayout>
                  <NotreEquipe />
                </AppLayout>
              } 
            />
            <Route 
              path="/contact" 
              element={
                <AppLayout>
                  <Contact />
                </AppLayout>
              } 
            />
            <Route 
              path="/devenir-chauffeur" 
              element={
                <AppLayout>
                  <DevenirChauffeur />
                </AppLayout>
              } 
            />
            <Route 
              path="/search" 
              element={
                <AppLayout>
                  <SearchResults />
                </AppLayout>
              } 
            />
            <Route 
              path="/payment" 
              element={
                <AppLayout>
                  <PaymentPage />
                </AppLayout>
              } 
            />
              <Route
                  path="/payment-return"
                  element={
                      <AppLayout>
                          <PaymentReturnPage />
                      </AppLayout>
                  }
              />
            <Route 
              path="/payment-success" 
              element={
                <AppLayout>
                  <PaymentSuccess />
                </AppLayout>
              } 
            />
            <Route 
              path="/mentions-legales" 
              element={
                <AppLayout>
                  <MentionsLegales />
                </AppLayout>
              } 
            />
            <Route 
              path="/conditions-generales" 
              element={
                <AppLayout>
                  <ConditionsGenerales />
                </AppLayout>
              } 
            />
            <Route 
              path="/politique-confidentialite" 
              element={
                <AppLayout>
                  <PolitiqueConfidentialite />
                </AppLayout>
              } 
            />
            <Route 
              path="/about" 
              element={
                <AppLayout>
                  <About />
                </AppLayout>
              } 
            />
            
            {/* Nouvelles routes d'authentification */}
            <Route 
              path="/connexion" 
              element={
                <AppLayout>
                  <Login />
                </AppLayout>
              } 
            />
            <Route 
              path="/inscription" 
              element={
                <AppLayout>
                  <Register />
                </AppLayout>
              } 
            />
            
            {/* Routes des tableaux de bord */}
            <Route 
              path="/dashboard/chauffeur" 
              element={
                <ProtectedRoute userType="driver">
                  <AppLayout>
                    <DriverDashboard />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/dashboard/passager" 
              element={
                <ProtectedRoute userType="passenger">
                  <AppLayout>
                    <PassengerDashboard />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            
            {/* Nouvelles routes pour le formulaire d'inscription et la page de confirmation */}
            <Route 
              path="/devenir-chauffeur/form" 
              element={
                <AppLayout>
                  <DriverApplicationForm />
                </AppLayout>
              } 
            />
            <Route 
              path="/devenir-chauffeur/confirmation" 
              element={
                <AppLayout>
                  <DriverApplicationConfirmation />
                </AppLayout>
              } 
            />
            
            {/* Routes du profil */}
            <Route 
              path="/profile" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <ProfilePage />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/profile/edit" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <EditProfile />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/profile/addresses" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <SavedAddresses />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/profile/payments" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <PaymentMethods />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/profile/change-password" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <ChangePassword />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/profile/language" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Language />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/profile/theme" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Theme />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/profile/referral" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Referral />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/profile/legal" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Legal />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/profile/help" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Help />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/profile/help/chat" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Chat />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/profile/help/phone" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Phone />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/profile/help/email" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Email />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/profile/help/faq" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <FAQ />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            
            {/* Routes de l'application */}
            <Route 
              path="/app" 
              element={
                <ProtectedRoute>
                  <AppLayout hideNavbar hideFooter>
                    <Home />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/app/book" 
              element={
                <ProtectedRoute>
                  <AppLayout hideNavbar hideFooter>
                    <Book />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/app/activity" 
              element={
                <ProtectedRoute>
                  <AppLayout hideNavbar hideFooter>
                    <Activity />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/app/favorites" 
              element={
                <ProtectedRoute>
                  <AppLayout hideNavbar hideFooter>
                    <Favorites />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/app/settings" 
              element={
                <ProtectedRoute>
                  <AppLayout hideNavbar hideFooter>
                    <Settings />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            
            {/* Redirection par défaut vers la page d'accueil */}
            <Route 
              path="*" 
              element={
                <AppLayout>
                  <Home />
                </AppLayout>
              } 
            />
          </Routes>
          
        </Suspense>
      </AppContainer>
    </LanguageProvider>
  );
}

export default App; 